import React, { useState } from "react";

import {
  faChevronDown,
  faChevronRight,
} from "@fortawesome/pro-regular-svg-icons";

import { useImpersonationWorkspaceFolders } from "src/adminPanel/queries";
import {
  grantsReachFolder,
  ScopedWorkspace,
} from "src/adminPanel/users/assumableRoleUtils";
import { Checkbox } from "src/base-components/Checkbox";
import { Icon } from "src/design-system/Icon";
import { Skeleton } from "src/design-system/Skeleton";
import { pluralize } from "src/utils/stringUtils";

type Props = {
  workspace: ScopedWorkspace;
  selectedFolderIds: string[];
  /** The role is selected with no explicit picks: the wildcard covers all. */
  isWildcard: boolean;
  isDisabled: boolean;
  onToggleFolder: (folderId: string, offeredFolderIds: string[]) => void;
};

/**
 * Per-workspace folder selection for a flow role. Collapsed by default so
 * expanding a role with many workspaces does not fetch every folder list.
 */
export const FolderScopePicker: React.FC<Props> = ({
  workspace,
  selectedFolderIds,
  isWildcard,
  isDisabled,
  onToggleFolder,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const folders = useImpersonationWorkspaceFolders(
    workspace.organizationId,
    workspace.id,
    isOpen,
  );

  const offered = (folders.data ?? []).filter((folder) =>
    grantsReachFolder(workspace.folderGrants, folder.id),
  );
  const offeredIds = offered.map((folder) => folder.id);
  const selectedHere = offered.filter((folder) =>
    selectedFolderIds.includes(folder.id),
  ).length;

  const isKnownEmpty =
    folders.data !== undefined && !folders.isError && offered.length === 0;

  if (isKnownEmpty) {
    return (
      <div
        className="ml-3"
        data-loc={`token-impersonation-folders-${workspace.id}`}
      >
        <p className="font-inter-normal-12px text-gray-500">
          No folders to choose from.
        </p>
      </div>
    );
  }

  return (
    <div
      className="ml-3"
      data-loc={`token-impersonation-folders-${workspace.id}`}
    >
      <button
        className="flex items-center gap-1 font-inter-normal-12px text-gray-500 hover:text-gray-800"
        data-loc={`token-impersonation-folders-toggle-${workspace.id}`}
        disabled={isDisabled}
        type="button"
        onClick={() => setIsOpen((current) => !current)}
      >
        <Icon icon={isOpen ? faChevronDown : faChevronRight} size="2xs" />
        {isWildcard
          ? "All folders"
          : selectedHere > 0
            ? `${pluralize(selectedHere, "folder")} selected`
            : "Choose folders"}
      </button>
      {isOpen &&
        (folders.isPending ? (
          <Skeleton height="h-4" rounded="rounded-lg" />
        ) : folders.isError ? (
          <p className="font-inter-normal-12px text-red-600">
            Could not load folders.
          </p>
        ) : (
          <ul className="mt-1 flex flex-col gap-1">
            {offered.map((folder) => (
              <li key={folder.id} className="flex items-center gap-2">
                <Checkbox
                  aria-label={`Select folder ${folder.name}`}
                  checked={isWildcard || selectedFolderIds.includes(folder.id)}
                  dataLoc={`token-impersonation-folder-${folder.id}`}
                  disabled={isDisabled}
                  onChange={() => onToggleFolder(folder.id, offeredIds)}
                />
                <span className="font-inter-normal-12px text-gray-600">
                  {folder.name}
                </span>
              </li>
            ))}
          </ul>
        ))}
    </div>
  );
};
